<script setup>
import {defaultApi} from '@/axios'
import GlobalLoadingBar from '@/components/GlobalLoadingBar.vue'
import ModalDialog from '@/components/ModalDialog.vue'
import SelectDefaultCompany from '@/components/SelectDefaultCompany.vue'
import SessionTimeout from '@/components/SessionTimeout.vue'
import Toast from '@/components/Toast.vue'
import {useGlobalStore} from '@/stores/global'
import {useUserStore} from '@/stores/user'
import {storeToRefs} from 'pinia'
import {computed, ref, watch} from 'vue'
import {version as currentVersion} from '../../../package.json'
import ExpiryCookie from '@/utils/ExpiryCookie.js'
import {provideUiVersion, useNewUiEnabled} from '@/composable/useNewUiEnabled'
import {useNewUiBodyClass} from '@/composable/useNewUiBodyClass'
import AssistantModal from '@/components/assistant/AssistantModal.vue'
import {useRoute} from 'vue-router'

const globalStore = useGlobalStore()
const userStore = useUserStore()
const {email} = storeToRefs(userStore)
const route = useRoute()
const routeUiVersion = computed(() => route.meta.uiVersion)
provideUiVersion(routeUiVersion)
const isNewUiEnabled = useNewUiEnabled(routeUiVersion)

const REFRESH_EVERY_11_HOURS = 1000 * 60 * 60 * 11

useNewUiBodyClass(true, routeUiVersion)

watch(
  () => [globalStore.selectedUsername],
  function () {
    window.charmVersion = currentVersion // needed to be read from app iframe
  }
)

if (location.host.startsWith('app')) {
  watch(email, function (newEmail) {
    if (newEmail != null) {
      window.hsConversationsSettings = {
        loadImmediately: false,
      }
      let _hsq = (window._hsq = window._hsq || [])
      _hsq.push([
        'identify',
        {
          email: newEmail,
        },
      ])
      _hsq.push(['trackPageView'])
      fetchHubspotToken()
      setInterval(() => fetchHubspotToken(), REFRESH_EVERY_11_HOURS)
    }
  })
}

function fetchHubspotToken() {
  const customData = {isExcludedFromLoadingBar: true}
  defaultApi
    .get('/api/user/hubspot-token', {customData})
    .then((response) => {
      window.hsConversationsSettings = {
        identificationEmail: userStore.email,
        identificationToken: response.data.token,
      }

      function onConversationsAPIReady() {
        window.HubSpotConversations.widget.load()
      }

      if (window.HubSpotConversations) {
        onConversationsAPIReady()
      } else {
        window.hsConversationsOnReady = [onConversationsAPIReady]
      }
    })
    .catch((error) => {
      console.log(error)
    })
}

const props = defineProps({
  initShowLoading: {
    type: Boolean,
    default: false,
  },
})

const showLoading = ref(props.initShowLoading)

watch(
  () => globalStore.pageLoading,
  function (val) {
    showLoading.value = val
  }
)
</script>

<template>
  <ModalDialog class="print:hidden"></ModalDialog>
  <Toast class="print:hidden"></Toast>
  <AssistantModal></AssistantModal>

  <router-view class="sticky top-0 left-0 z-header print:hidden" name="Header"></router-view>

  <div class="w-full h-[calc(100vh-80px)] flex print:h-auto print:block">
    <main class="overflow-y-auto w-full h-full flex-grow flex flex-col print:h-auto print:block print:overflow-visible">
      <div class="w-full h-max grow flex flex-col">
        <GlobalLoadingBar class="absolute z-loading print:hidden" />
        <router-view></router-view>
      </div>

      <router-view class="print:hidden" name="Footer"></router-view>
    </main>

    <aside v-if="!isNewUiEnabled" class="flex-shrink-0 h-full overflow-y-auto shadow-md print:hidden"></aside>
  </div>

  <SessionTimeout class="print:hidden"></SessionTimeout>
  <SelectDefaultCompany v-if="ExpiryCookie.isAuthenticated()" class="print:hidden"></SelectDefaultCompany>
</template>

<style scoped>
@media (max-width: 1366px) {
  aside {
    position: absolute;
    right: 0;
    height: calc(100dvh - 80px);
  }
}
</style>
